import {
  Button,
  Flex,
  Grid,
  GridItem,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
} from "@chakra-ui/react";
import { useContext, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { fetchController } from "../../utils/FetchController/fetchController";
import { getHostDomain } from "../../utils/utils";
import { AgencyContext } from "../Settings/AgencyProvider/AgencyProvider";
import FormLabelWithInfoTooltip from "../Widgets/FormLabelWithInfoTooltip";

const AddCustomBillingPlanWithWalletFields = ({
  isOpen,
  onClose,
  fetchAgencyBillingPlanList,
  tableData = [],
  itemToUpdate = null,
  onAgencyUpdated, // optional parent callback to update context/state
}) => {
  const baseUrl = getHostDomain();
  const [isLoading, setIsLoading] = useState(false);
  const finalRef = useRef(null);
  const agency = useContext(AgencyContext);
  const { agencyData, textColor, buttonColorScheme } = agency || {};

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      default_wallet_balance: 5,
      wallet_currency: "usd",
    },
  });

  const populateForm = (source = {}) => {
    if (itemToUpdate) {
      if (itemToUpdate.name !== undefined) setValue("name", itemToUpdate.name);
      if (itemToUpdate.default_wallet_balance !== undefined)
        setValue("default_wallet_balance", itemToUpdate.default_wallet_balance);
      if (itemToUpdate.wallet_currency !== undefined)
        setValue("wallet_currency", itemToUpdate.wallet_currency);
      return;
    }

    if (source) {
      if (source.name !== undefined) setValue("name", source.name);
      if (source.subaccount_wallet?.default_wallet_balance !== undefined)
        setValue(
          "default_wallet_balance",
          source.subaccount_wallet.default_wallet_balance
        );
      if (source.subaccount_wallet?.wallet_currency !== undefined)
        setValue("wallet_currency", source.subaccount_wallet.wallet_currency);
    }
  };

  // Fetch agency details from server when modal opens and populate form
  useEffect(() => {
    const loadFromApi = async () => {
      if (!agencyData?.id) return;
      setIsLoading(true);
      try {
        const res = await fetchController(
          baseUrl + `/api/v1/agency/${agencyData.id}`,
          "GET"
        );
        const payload = res?.data ?? res;
        const billing = payload?.billing_plan ?? payload?.billing ?? {};
        const name = payload?.branding?.agency_name ?? payload?.name;

        const source = {
          name,
          ...billing,
        };

        populateForm(source);
      } catch (e) {
        if (agencyData) {
          const billing = agencyData.billing_plan || {};
          const name = agencyData?.branding?.agency_name ?? agencyData.name;
          populateForm({ name, ...billing });
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      loadFromApi();
    }
  }, [isOpen, agencyData?.id, itemToUpdate]);

  const onSubmit = async (formData) => {
    try {
      if (!agencyData?.id) {
        toast.error("Agency ID missing");
        return;
      }

      setIsLoading(true);
      const billingPlan = agencyData?.billing_plan || {};
      // Build nested payload without auto-recharge fields
      const payload = {
        billing_plan: {
          ...billingPlan,
          subaccount_wallet: {
            // copy existing subaccount_wallet fields (if any)
            ...(billingPlan.subaccount_wallet || {}),
            // ensure multipliers copied safely
            multipliers: {
              ...(billingPlan.subaccount_wallet?.multipliers || {}),
            },
            // override or set these two fields from form
            default_wallet_balance:
              Number(formData.default_wallet_balance) || 0,
            wallet_currency: formData.wallet_currency || "",
          },
        },
      };
      // PUT update request
      const response = await fetchController(
        baseUrl + `/api/v1/agency/${agencyData.id}`,
        "PUT",
        payload
      );

      toast.success(
        response?.message || "Agency wallet settings updated successfully"
      );

      // Refresh agency data after update
      try {
        const freshRes = await fetchController(
          baseUrl + `/api/v1/agency/${agencyData.id}`,
          "GET"
        );
        const latestAgency = freshRes?.data ?? freshRes;
        if (latestAgency) {
          if (agency && typeof agency.setAgencyData === "function") {
            agency.setAgencyData(latestAgency);
          }
          if (typeof onAgencyUpdated === "function") {
            onAgencyUpdated(latestAgency);
          }
        }
      } catch (e) {
        console.warn("Failed to re-fetch agency after PUT:", e);
      }

      onClose();
      reset();

      if (fetchAgencyBillingPlanList) await fetchAgencyBillingPlanList();
    } catch (error) {
      toast.error("Oops! Something went wrong while updating agency.");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      size="md"
      finalFocusRef={finalRef}
      isOpen={isOpen}
      onClose={() => onClose()}
    >
      <ModalOverlay />
      <ModalContent height="auto" width="95%" maxWidth="700px">
        <ModalHeader color={textColor}>Wallet Settings</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {isLoading ? (
            <GridItem colSpan={4}>
              <Flex justifyContent={"center"}>
                <Spinner size="lg" />
              </Flex>
            </GridItem>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid
                templateColumns="repeat(4, 1fr)"
                rowGap={4}
                columnGap={4}
                gap={4}
              >
                <GridItem colSpan={2}>
                  <FormLabelWithInfoTooltip
                    label="Default Wallet Balance"
                    tip="Initial balance users will receive in their wallet."
                  />
                  <Input
                    type="number"
                    step="0.01"
                    {...register("default_wallet_balance", { required: true })}
                  />
                  {errors.default_wallet_balance && (
                    <p style={{ color: "red", fontSize: "11px" }}>
                      Default Wallet Balance is required
                    </p>
                  )}
                </GridItem>

                <GridItem colSpan={2}>
                  <FormLabelWithInfoTooltip
                    label="Currency (ex: usd)"
                    tip="Currency code for the wallet (lowercase)"
                  />
                  <Input
                    placeholder="usd"
                    {...register("wallet_currency", { required: true })}
                  />
                  {errors.wallet_currency && (
                    <p style={{ color: "red", fontSize: "11px" }}>
                      Currency is required
                    </p>
                  )}
                </GridItem>
              </Grid>

              <Flex justifyContent={{ base: "stretch", md: "end" }} mt={4}>
                <Button
                  isLoading={isSubmitting}
                  width="100px"
                  colorScheme={buttonColorScheme}
                  type="submit"
                >
                  Save
                </Button>
              </Flex>
            </form>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AddCustomBillingPlanWithWalletFields;
